import { useAuthStore } from '../stores/auth'
import type {
  AccessEvent,
  AccessGroup,
  AccessGroupBody,
  AccessGroupMember,
  AccessSystem,
  AuditEntry,
  Changelog,
  AuthMethodsResponse,
  AvailableSite,
  AxDoor,
  AxDoorControllerBindResult,
  AxPendingController,
  CapabilityResolution,
  CommandLog,
  CreateSystemBody,
  Credential,
  CredentialPatchResult,
  CredentialOrder,
  CustomerGrant,
  Device,
  DeviceUpdateBody,
  DuplicateGroup,
  MergeDuplicatesBody,
  MergeDuplicatesResult,
  MergePreview,
  MergeVerification,
  DirectAccessGroupMember,
  DirectoryGrant,
  DirectoryGroupEffectiveAccess,
  Door,
  DoorCode,
  DoorCodeBody,
  DoorCodeSeries,
  DoorCodeSeriesBody,
  DoorCodeSeriesPatchBody,
  DoorCreateBody,
  DoorPolicy,
  DoorPolicyUpdateBody,
  DoorUpdateBody,
  EdgeGateway,
  EdgeInstance,
  EdgeInstanceBody,
  EdgeLogEntry,
  EffectiveDoor,
  EventsParams,
  GatewayCreateResult,
  GatewayUpdateResult,
  Group,
  GroupBody,
  GroupNode,
  GroupOccupancy,
  IdentityTokenResponse,
  InviteResult,
  LocationBinding,
  LocationCredentialUsage,
  LocationDetail,
  LocationNode,
  LocationPayload,
  LoginResponse,
  MappingBoard,
  MappingDecideBody,
  MappingDecideBulkBody,
  MappingDecideBulkResponse,
  MappingItem,
  MappingSite,
  MappingSplitOnBody,
  MappingSplitOnResponse,
  MappingSummary,
  MatchSuggestion,
  MemberRule,
  Occupancy,
  Operator,
  OAuthAuthorizeResponse,
  OAuthCallbackBody,
  Page,
  Passkey,
  ProviderCapabilities,
  PasskeyLoginVerifyResult,
  PasskeyOptionsResult,
  PasskeyRegisterResult,
  Person,
  PersonDetail,
  PersonEffectiveAccess,
  Principal,
  Profile,
  RecoveryCodesResult,
  RollSummary,
  Schedule,
  ScheduleBody,
  ScheduleKind,
  ServiceAccountResult,
  StepUpChallengeResult,
  StepUpTokenResult,
  SyncAllResult,
  SyncRun,
  SystemIntegration,
  SystemIntegrationCreateBody,
  SystemIntegrationUpdateBody,
  SystemProvider,
  TlsCertificate,
  TokenResponse,
  TotpConfirmResult,
  TotpDevice,
  TotpEnrollResult,
  TrashListResponse,
  UnknownCredential,
  VendorRow,
  VendorTable,
  WebhookDelivery,
  WebhookSubscription,
  WebhookSubscriptionBody,
  EventTypeChoice,
  CameraForwardingSettings,
  CameraForwardingSettingsBody,
  EventBookmark,
  ForwardingDefaults,
  Zone,
  ZonePayload,
} from './types'

// Dev talks to the vite proxy at /api (override with VITE_API_URL); a
// production build MUST NOT silently fall back to same-origin -- a bare
// `npm run build` + wrangler deploy once shipped a console calling
// ostrel.app/api/* (405s everywhere). Prod default is the real API.
export const BASE: string = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? '/api' : 'https://api.ostrel.se')

/** Host shown in the sidebar footer (e.g. api.ostrel.se). */
export const API_HOST: string = BASE.startsWith('http') ? new URL(BASE).host : window.location.host

export class ApiError extends Error {
  readonly status: number
  readonly detail: string
  /** Present on a 403 step_up_required body: the factors the user can
   *  complete to step up (tenancy/stepup.py). */
  readonly methods: string[]
  /** The body's `error` taxonomy code when present (e.g. `preview_stale`,
   *  `unacknowledged_warnings`, `busy`), else ''. */
  readonly code: string
  /** Seconds from a Retry-After header (503 busy), else null. */
  readonly retryAfter: number | null

  constructor(status: number, detail: string, methods: string[] = [], code = '', retryAfter: number | null = null) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.methods = methods
    this.code = code
    this.retryAfter = retryAfter
  }

  /** True for the 403 {"detail": "step_up_required"} gate response. */
  get isStepUpRequired(): boolean {
    return this.status === 403 && this.detail === 'step_up_required'
  }
}

type AuthScheme = 'none' | 'identity' | 'customer'

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  auth?: AuthScheme
  /** Step-up token (sudo mode) sent as the X-Step-Up header. */
  stepUp?: string
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = 'customer', stepUp } = options

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (stepUp) headers['X-Step-Up'] = stepUp
  if (auth !== 'none') {
    const state = useAuthStore.getState()
    const token = auth === 'identity' ? state.identityToken : state.accessToken
    if (token) headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401 && auth === 'customer') {
    // Customer tokens have no refresh — bounce to re-login.
    useAuthStore.getState().expireSession()
    if (window.location.pathname !== '/login') window.location.assign('/login')
    throw new ApiError(401, 'Session expired')
  }

  if (!res.ok) {
    let detail = `Request failed (${res.status})`
    let detailResolved = false
    let methods: string[] = []
    let code = ''
    const retryAfterHeader = Number.parseInt(res.headers?.get?.('Retry-After') ?? '', 10)
    const retryAfter = Number.isFinite(retryAfterHeader) ? retryAfterHeader : null
    try {
      const data: unknown = await res.json()
      if (data && typeof data === 'object' && 'detail' in data) {
        const d = (data as { detail: unknown }).detail
        if (typeof d === 'string' && d !== '') {
          detail = d
          detailResolved = true
        } else if (Array.isArray(d)) {
          // 422 validation errors: detail is a list of strings or {msg} objects.
          const parts = d.map((item: unknown) => {
            if (typeof item === 'string') return item
            if (item && typeof item === 'object' && 'msg' in item) return String((item as { msg: unknown }).msg)
            return JSON.stringify(item)
          })
          if (parts.length > 0) {
            detail = parts.join('; ')
            detailResolved = true
          }
        }
      }
      if (data && typeof data === 'object' && 'error' in data) {
        const e = (data as { error: unknown }).error
        if (typeof e === 'string') code = e
      }
      if (!detailResolved && data && typeof data === 'object' && 'error' in data) {
        // The mapping endpoints' ErrorOut shape is {"error": <taxonomy
        // string>, "detail": <human reason, optional>} (e.g. already_adopted,
        // capability_not_supported) — the human detail won above when present
        // and non-empty; otherwise surface the taxonomy instead of the bare
        // status.
        const e = (data as { error: unknown }).error
        if (typeof e === 'string') detail = e
      }
      // Step-up gate: the body also carries the user's completable factors.
      if (data && typeof data === 'object' && 'methods' in data) {
        const m = (data as { methods: unknown }).methods
        if (Array.isArray(m)) methods = m.filter((x): x is string => typeof x === 'string')
      }
    } catch {
      // A non-JSON 502/503 is the edge answering while the API restarts
      // (deploy window) — say so instead of a bare status or a parse error.
      if (res.status === 502 || res.status === 503) detail = 'Ostrel is updating — retry shortly'
    }
    throw new ApiError(res.status, detail, methods, code, retryAfter)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/**
 * Raw customer-scoped GET: returns the Response with the body UNREAD (the
 * caller decides to stream it, blob it, or cancel it). Errors still surface
 * as ApiError (the backend answers failures with the usual {"detail": …}
 * JSON body).
 */
async function requestRaw(path: string): Promise<Response> {
  const headers: Record<string, string> = {}
  const token = useAuthStore.getState().accessToken
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { headers })

  if (res.status === 401) {
    useAuthStore.getState().expireSession()
    if (window.location.pathname !== '/login') window.location.assign('/login')
    throw new ApiError(401, 'Session expired')
  }

  if (!res.ok) {
    let detail = `Request failed (${res.status})`
    try {
      const data: unknown = await res.json()
      if (data && typeof data === 'object' && 'detail' in data) {
        const d = (data as { detail: unknown }).detail
        if (typeof d === 'string') detail = d
      }
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail)
  }

  return res
}

/**
 * Binary download counterpart of `request` (customer-scoped auth): returns the
 * response as a Blob instead of parsing JSON. `onProgress` fires with the
 * cumulative byte count as the body streams in.
 */
async function requestBlob(path: string, onProgress?: (bytes: number) => void): Promise<Blob> {
  const res = await requestRaw(path)

  if (!res.body || !onProgress) return res.blob()

  const reader = res.body.getReader()
  const chunks: BlobPart[] = []
  let received = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    received += value.byteLength
    onProgress(received)
  }
  return new Blob(chunks)
}

function qs(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value)
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

export const api = {
  // --- Auth (two-step JWT) ---
  authMethods: () => request<AuthMethodsResponse>('/auth/methods', { auth: 'none' }),
  /** Public: status + running API release (version shown on the login page). */
  health: () => request<{ status: string; version?: string }>('/healthz', { auth: 'none' }),
  login: (username: string, password: string) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: { username, password }, auth: 'none' }),
  loginService: (serviceAccount: string, secret: string) =>
    request<LoginResponse>('/auth/login-service', {
      method: 'POST',
      body: { service_account: serviceAccount, secret },
      auth: 'none',
    }),
  token: (customer: string) =>
    request<TokenResponse>('/auth/token', { method: 'POST', body: { customer }, auth: 'identity' }),
  customers: () => request<CustomerGrant[]>('/auth/customers', { auth: 'identity' }),
  me: () => request<Principal>('/me', { auth: 'identity' }),
  /** Own profile basics (first/last name only — email/username are the
   *  immutable login identity). Returns the updated Principal. */
  patchMe: (body: { first_name?: string; last_name?: string }) =>
    request<Principal>('/me', { method: 'PATCH', body, auth: 'identity' }),
  /** Own password. 401 = current password wrong; 422 = policy reasons;
   *  403 step_up_required when the user holds MFA factors (pass stepUp). */
  changePassword: (body: { current_password: string; new_password: string }, stepUp?: string) =>
    request<{ success: boolean }>('/auth/change-password', { method: 'POST', body, auth: 'identity', stepUp }),

  // --- Step-up ("sudo mode", tenancy/stepup.py) ---
  stepUpChallenge: () =>
    request<StepUpChallengeResult>('/auth/step-up/challenge', { method: 'POST', auth: 'identity' }),
  stepUpVerify: (body: { challenge_id: string; totp_code?: string; recovery_code?: string }) =>
    request<StepUpTokenResult>('/auth/step-up/verify', { method: 'POST', body, auth: 'identity' }),
  oauthAuthorize: (provider: string) =>
    request<OAuthAuthorizeResponse>(`/auth/oauth/${provider}/authorize`, { auth: 'none' }),
  oauthCallback: (provider: string, body: OAuthCallbackBody) =>
    request<LoginResponse>(`/auth/oauth/${provider}/callback`, { method: 'POST', body, auth: 'none' }),

  // --- Passkeys & MFA (tenancy/mfa_api.py) ---
  /** Unauthenticated: start a passkey login (email hint optional; an
   *  mfa_challenge id binds the ceremony as a password login's 2nd factor,
   *  a step_up_challenge id as the step-up flow's passkey proof). */
  passkeyLoginOptions: (body: { email?: string; mfa_challenge?: string; step_up_challenge?: string }) =>
    request<PasskeyOptionsResult>('/auth/passkey/login/options', { method: 'POST', body, auth: 'none' }),
  passkeyLoginVerify: (body: { challenge_id: string; credential: unknown }) =>
    request<PasskeyLoginVerifyResult>('/auth/passkey/login/verify', { method: 'POST', body, auth: 'none' }),
  /** Unauthenticated: complete an MFA challenge with TOTP or a recovery code
   *  (exactly one of the two). The challenge is single-use — a wrong code
   *  consumes it and the password step must be redone. */
  mfaVerify: (body: { challenge_id: string; totp_code?: string; recovery_code?: string }) =>
    request<IdentityTokenResponse>('/auth/mfa/verify', { method: 'POST', body, auth: 'none' }),
  // Enrollment: IdentityAuth (the caller's OWN user — factors are user-global).
  // All mutating calls here are step-up gated once the user holds a factor;
  // pass the X-Step-Up token via `stepUp` (useStepUp handles this).
  passkeys: () => request<Passkey[]>('/auth/passkeys', { auth: 'identity' }),
  passkeyRegisterOptions: (name: string, stepUp?: string) =>
    request<PasskeyOptionsResult>('/auth/passkeys/register/options', {
      method: 'POST',
      body: { name },
      auth: 'identity',
      stepUp,
    }),
  passkeyRegisterVerify: (body: { challenge_id: string; credential: unknown; name: string }, stepUp?: string) =>
    request<PasskeyRegisterResult>('/auth/passkeys/register/verify', {
      method: 'POST',
      body,
      auth: 'identity',
      stepUp,
    }),
  deletePasskey: (uid: string, stepUp?: string) =>
    request<{ success: boolean }>(`/auth/passkeys/${uid}`, { method: 'DELETE', auth: 'identity', stepUp }),
  totpDevices: () => request<TotpDevice[]>('/auth/totp', { auth: 'identity' }),
  totpEnroll: (name: string, stepUp?: string) =>
    request<TotpEnrollResult>('/auth/totp/enroll', { method: 'POST', body: { name }, auth: 'identity', stepUp }),
  totpConfirm: (device: string, code: string, stepUp?: string) =>
    request<TotpConfirmResult>('/auth/totp/confirm', {
      method: 'POST',
      body: { device, code },
      auth: 'identity',
      stepUp,
    }),
  deleteTotp: (uid: string, stepUp?: string) =>
    request<{ success: boolean }>(`/auth/totp/${uid}`, { method: 'DELETE', auth: 'identity', stepUp }),
  /** Regenerates all recovery codes (old ones die); plaintext returned once. */
  regenerateRecoveryCodes: (stepUp?: string) =>
    request<RecoveryCodesResult>('/auth/recovery-codes', { method: 'POST', auth: 'identity', stepUp }),

  // --- Profile ---
  profile: () => request<Profile>('/profile'),
  profileCustomers: () => request<CustomerGrant[]>('/profile/customers'),
  profileActivity: (cursor?: string) => request<Page<AuditEntry>>(`/profile/activity${qs({ cursor })}`),

  // --- Operators ---
  operators: () => request<Operator[]>('/operators'),
  inviteOperator: (body: { email: string; role: string; first_name?: string; last_name?: string }) =>
    request<InviteResult>('/operators/invite', { method: 'POST', body }),
  setOperatorRole: (uid: string, role: string) =>
    request<Operator>(`/operators/${uid}/role`, { method: 'PUT', body: { role } }),
  setOperatorAllowedIps: (uid: string, allowedIps: string[]) =>
    request<Operator>(`/operators/${uid}/allowed-ips`, { method: 'PUT', body: { allowed_ips: allowedIps } }),
  removeOperator: (uid: string) => request<void>(`/operators/${uid}`, { method: 'DELETE' }),
  transferOwnership: (uid: string) =>
    request<void>(`/operators/${uid}/transfer-ownership`, { method: 'POST' }),
  createServiceAccount: (name: string, role: string, allowedIps: string[]) =>
    request<ServiceAccountResult>('/operators/service-accounts', {
      method: 'POST',
      body: { name, role, allowed_ips: allowedIps },
    }),
  rotateServiceAccount: (uid: string) =>
    request<{ secret: string }>(`/operators/service-accounts/${uid}/rotate`, { method: 'POST' }),

  // --- Changelog ---
  changelog: () => request<Changelog>('/changelog'),

  // --- Audit ---
  audit: (filters: { category?: string; action?: string; target?: string; actor?: string; since?: string; cursor?: string }) =>
    request<Page<AuditEntry>>(`/audit${qs(filters)}`),

  // --- Data endpoints ---
  systems: () => request<AccessSystem[]>('/systems'),
  createSystem: (body: CreateSystemBody) =>
    request<AccessSystem>('/systems', { method: 'POST', body }),
  updateSystem: (uid: string, body: CreateSystemBody) =>
    request<AccessSystem>(`/systems/${uid}`, { method: 'PUT', body }),
  verifySystem: (uid: string) =>
    request<AccessSystem>(`/systems/${uid}/verify`, { method: 'POST' }),
  deleteSystem: (uid: string) =>
    request<{ success: boolean }>(`/systems/${uid}`, { method: 'DELETE' }),
  fetchSystemCertificate: (uid: string) =>
    request<TlsCertificate>(`/systems/${uid}/tls/fetch`, { method: 'POST' }),
  pinSystemCertificate: (uid: string, sha256: string) =>
    request<AccessSystem>(`/systems/${uid}/tls/pin`, { method: 'POST', body: { sha256 } }),
  unpinSystemCertificate: (uid: string) =>
    request<AccessSystem>(`/systems/${uid}/tls/pin`, { method: 'DELETE' }),
  availableSites: (uid: string) => request<AvailableSite[]>(`/systems/${uid}/available-sites`),
  setManagedSites: (uid: string, sites: AvailableSite[]) =>
    request<AvailableSite[]>(`/systems/${uid}/managed-sites`, { method: 'PUT', body: sites }),
  managedSites: (uid: string) => request<AvailableSite[]>(`/systems/${uid}/managed-sites`),
  locationBindings: (uid: string) => request<LocationBinding[]>(`/systems/${uid}/location-bindings`),
  bindLocation: (uid: string, body: { location: string; site_key?: string }) =>
    request<LocationBinding>(`/systems/${uid}/location-bindings`, { method: 'POST', body }),
  unbindLocation: (uid: string, bindingUid: string) =>
    request<{ success: boolean }>(`/systems/${uid}/location-bindings/${bindingUid}`, { method: 'DELETE' }),
  syncRuns: (system?: string) => request<Page<SyncRun>>(`/sync-runs${qs({ system })}`),
  /** Manual full vendor→us pull (POST /systems/{uid}/sync): enqueues, 409s
   *  {'error': 'system is disabled'} on a disabled system. */
  syncSystemNow: (uid: string) =>
    request<{ enqueued: string }>(`/systems/${uid}/sync`, { method: 'POST' }),
  /** Manual full sync for every active, non-locked system of a provider
   *  (POST /systems/{provider}/sync-all). */
  syncAllSystems: (provider: SystemProvider) =>
    request<SyncAllResult>(`/systems/${provider}/sync-all`, { method: 'POST' }),
  /** Manual us→vendor push (POST /systems/{uid}/push): enqueues a
   *  reconcile pass right now instead of waiting for the next dispatch
   *  tick. Same 409 shape as syncSystemNow. */
  pushSystemNow: (uid: string) =>
    request<{ enqueued: string }>(`/systems/${uid}/push`, { method: 'POST' }),

  // --- System integrations ---
  systemIntegrations: (uid: string) => request<SystemIntegration[]>(`/systems/${uid}/integrations`),
  systemIntegration: (uid: string, iid: string) =>
    request<SystemIntegration>(`/systems/${uid}/integrations/${iid}`),
  createSystemIntegration: (uid: string, body: SystemIntegrationCreateBody) =>
    request<SystemIntegration>(`/systems/${uid}/integrations`, { method: 'POST', body }),
  /** Full-replace EXCEPT secrets (omitted = keep, {} = clear). */
  updateSystemIntegration: (uid: string, iid: string, body: SystemIntegrationUpdateBody) =>
    request<SystemIntegration>(`/systems/${uid}/integrations/${iid}`, { method: 'PUT', body }),
  deleteSystemIntegration: (uid: string, iid: string) =>
    request<{ success: boolean }>(`/systems/${uid}/integrations/${iid}`, { method: 'DELETE' }),
  /** The resolved routing table: capability → winning integration + why. */
  providerCapabilities: () => request<Record<string, ProviderCapabilities>>('/capabilities'),
  systemCapabilities: (uid: string) =>
    request<Record<string, CapabilityResolution>>(`/systems/${uid}/capabilities`),

  // --- Mapping board (the mapping guide; 400 capability_not_supported when
  // the connector has no SupportsAdoption) ---
  mappingBoard: (uid: string, site?: string) =>
    request<MappingBoard>(`/systems/${uid}/mapping${qs({ site })}`),
  mappingSummary: (uid: string, site?: string) =>
    request<Record<string, MappingSummary>>(`/systems/${uid}/mapping/summary${qs({ site })}`),
  /** Site list with per-site progress; [] for single-site providers (the
   *  board/summary take no site filter then). */
  mappingSites: (uid: string) => request<MappingSite[]>(`/systems/${uid}/mapping/sites`),
  /** Record one decision; the response is the item as the board now shows it.
   *  409 already_adopted, 404 unknown vendor_uid/target, 400 kind_mismatch /
   *  writes_not_allowed / invalid_vendor_data. */
  mappingDecide: (uid: string, body: MappingDecideBody) =>
    request<MappingItem>(`/systems/${uid}/mapping/decide`, { method: 'POST', body }),
  /** One decision applied to many vendor rows in a single POST; per-row
   *  outcomes come back in `results` (a poison row never aborts the batch). */
  mappingDecideBulk: (uid: string, body: MappingDecideBulkBody) =>
    request<MappingDecideBulkResponse>(`/systems/${uid}/mapping/decide-bulk`, { method: 'POST', body }),
  /** Split-on tree import: ONE POST for the whole selection — per row the
   *  server finds-or-creates the location + group chains and attaches the
   *  vendor row to the leaf group. Per-row outcomes in `results`. */
  mappingSplitOn: (uid: string, body: MappingSplitOnBody) =>
    request<MappingSplitOnResponse>(`/systems/${uid}/mapping/split-on`, { method: 'POST', body }),
  /** Canonical devices (optionally scoped to one system). */
  devices: (system?: string) => request<Device[]>(`/devices${qs({ system })}`),
  device: (uid: string) => request<Device>(`/devices/${uid}`),
  updateDevice: (uid: string, body: DeviceUpdateBody) =>
    request<Device>(`/devices/${uid}`, { method: 'PATCH', body }),

  // --- Vendor raw-data browser (read-only vendor-mirror tables) ---
  vendorDataTables: (uid: string) => request<VendorTable[]>(`/systems/${uid}/vendor-data`),
  vendorDataRows: (uid: string, table: string, cursor?: string) =>
    request<Page<VendorRow>>(`/systems/${uid}/vendor-data/${table}${qs({ cursor })}`),

  // --- Axema connector (vendor mirror view) ---
  /** AxDoor mirror rows for one Axema system (includes the operator-bound
   *  DCIP controller MAC per door). */
  axemaDoors: (uid: string) => request<AxDoor[]>(`/connectors/axema/systems/${uid}/doors`),
  /** Pending DCIP controller sightings (the bind pick-list): deduped by MAC,
   *  newest-first, bound MACs excluded. Operator+ only — 403s viewers. */
  axemaPendingControllers: (uid: string) =>
    request<AxPendingController[]>(`/connectors/axema/systems/${uid}/pending-controllers`),
  /** Bind a DCIP controller MAC to an AxDoor (operator role; audit-logged;
   *  the appliance picks it up on its next directory refresh). `mac: ''`
   *  unbinds. 422 on a malformed MAC, 404 on unknown/not-found doors. */
  bindAxemaDoorController: (uid: string, doorUid: string, mac: string) =>
    request<AxDoorControllerBindResult>(`/connectors/axema/systems/${uid}/doors/${doorUid}/controller`, {
      method: 'POST',
      body: { mac },
    }),

  // --- Edge (gateways & instances) ---
  edgeGateways: () => request<EdgeGateway[]>('/edge/gateways'),
  /** Streams the gateway installer ISO as an attachment (customer-admin).
   *  404 {detail: 'no installer ISO published yet'} when none is published. */
  downloadEdgeIso: (onProgress?: (bytes: number) => void) => requestBlob('/edge/iso', onProgress),
  /** Cheap availability probe for the installer ISO: same request, but the
   *  body is cancelled as soon as the headers arrive. Resolves when an ISO is
   *  published, throws ApiError (404 = none published) otherwise. */
  probeEdgeIso: async () => {
    const res = await requestRaw('/edge/iso')
    await res.body?.cancel()
  },
  /** Streams the Proxmox appliance image (qcow2) as an attachment
   *  (customer-admin). 404 {detail: 'no Proxmox image published yet'} when
   *  none is published. */
  downloadEdgeImage: (onProgress?: (bytes: number) => void) => requestBlob('/edge/image', onProgress),
  /** Cheap availability probe for the Proxmox image — mirror of
   *  probeEdgeIso (404 = none published). */
  probeEdgeImage: async () => {
    const res = await requestRaw('/edge/image')
    await res.body?.cancel()
  },
  /** Customer-admin provisioning (the customer comes from the token): returns
   *  the plaintext enroll token exactly once. `allowed_ips` is the optional
   *  source-IP allowlist (CIDRs; omitted/empty = any source). */
  createGateway: (body: { name: string; allowed_ips?: string[] }) =>
    request<GatewayCreateResult>('/gateways', { method: 'POST', body }),
  /** Partial update: name and/or the source-IP allowlist ([] clears it). */
  updateGateway: (uid: string, body: { name?: string; allowed_ips?: string[] }) =>
    request<GatewayUpdateResult>(`/gateways/${uid}`, { method: 'PATCH', body }),
  /** The reinstall path: rotates the enroll token (row/instances/logs kept),
   *  plaintext returned exactly once. The body is optional — a reinstall is
   *  also when a moved appliance's allowlist changes. */
  reenrollGateway: (uid: string, body?: { allowed_ips?: string[]; name?: string }) =>
    request<GatewayCreateResult>(`/gateways/${uid}/re-enroll`, { method: 'POST', body }),
  /** 409 while the gateway is connected or still has edge instances. */
  deleteGateway: (uid: string) =>
    request<{ success: boolean }>(`/gateways/${uid}`, { method: 'DELETE' }),
  /** No `gateway` filter param server-side — filter client-side where needed. */
  edgeInstances: () => request<EdgeInstance[]>('/edge/instances'),
  createEdgeInstance: (body: EdgeInstanceBody) =>
    request<EdgeInstance>('/edge/instances', { method: 'POST', body }),
  /** Full-replace EXCEPT secrets (omitted = keep, {} = clear). */
  updateEdgeInstance: (uid: string, body: EdgeInstanceBody) =>
    request<EdgeInstance>(`/edge/instances/${uid}`, { method: 'PUT', body }),
  deleteEdgeInstance: (uid: string) =>
    request<{ success: boolean }>(`/edge/instances/${uid}`, { method: 'DELETE' }),
  /** 409 when the gateway holds no live connection. */
  restartEdgeInstance: (uid: string) =>
    request<{ success: boolean; command_id: string }>(`/edge/instances/${uid}/restart`, { method: 'POST' }),
  edgeGatewayLogs: (uid: string, cursor?: string) =>
    request<Page<EdgeLogEntry>>(`/edge/gateways/${uid}/logs${qs({ cursor })}`),
  persons: (q?: string, location?: string) =>
    request<Person[]>(`/persons${qs({ q, location })}`),
  person: (uid: string) => request<PersonDetail>(`/persons/${uid}`),
  createPerson: (body: {
    first_name?: string
    last_name?: string
    emails?: string[]
    phones?: string[]
    status?: string
    external_ref?: string
    note?: string
    groups?: string[]
    valid_from?: string | null
    valid_to?: string | null
    show_on_intercom?: boolean
    mailbox_access?: boolean
  }) => request<Person>('/persons', { method: 'POST', body }),
  updatePerson: (
    uid: string,
    body: {
      first_name?: string
      last_name?: string
      emails?: string[]
      phones?: string[]
      status?: string
      external_ref?: string
      note?: string
      groups?: string[]
      valid_from?: string | null
      valid_to?: string | null
      show_on_intercom?: boolean
      mailbox_access?: boolean
    },
  ) => request<Person>(`/persons/${uid}`, { method: 'PUT', body }),
  deletePerson: (uid: string) => request<void>(`/persons/${uid}`, { method: 'DELETE' }),
  mergePerson: (uid: string, target: string) =>
    request<Person>(`/persons/${uid}/merge`, { method: 'POST', body: { target } }),
  /** Detected duplicate groups (any role). */
  personDuplicates: () => request<DuplicateGroup[]>('/persons/duplicates'),
  /** The duplicate group this person belongs to, or null (any role). */
  personDuplicateGroup: (uid: string) => request<{ group: DuplicateGroup | null }>(`/persons/${uid}/duplicates`),
  /** Rolled-back simulation of a merge (operator). `groups` is the per-group
   *  selection: undefined = the union, [] = an explicitly empty selection
   *  (sent as `groups=`, which qs() would drop). */
  mergePreview: (persons: string[], survivor?: string, groups?: string[]) => {
    const search = new URLSearchParams({ persons: persons.join(',') })
    if (survivor) search.set('survivor', survivor)
    if (groups !== undefined) search.set('groups', groups.join(','))
    return request<MergePreview>(`/persons/merge-preview?${search.toString()}`)
  },
  mergeDuplicates: (body: MergeDuplicatesBody) =>
    request<MergeDuplicatesResult>('/persons/merge-duplicates', { method: 'POST', body }),
  mergeVerification: (survivor: string, verification: string) =>
    request<MergeVerification>(`/persons/${survivor}/merge-verification/${verification}`),
  groups: (q?: string) => request<Group[]>(`/groups${qs({ q })}`),
  groupsTree: () => request<GroupNode[]>('/groups/tree'),
  group: (uid: string) => request<Group>(`/groups/${uid}`),
  createGroup: (body: GroupBody) => request<Group>('/groups', { method: 'POST', body }),
  updateGroup: (uid: string, body: GroupBody) =>
    request<Group>(`/groups/${uid}`, { method: 'PUT', body }),
  deleteGroup: (uid: string) => request<{ success: boolean }>(`/groups/${uid}`, { method: 'DELETE' }),
  mergeGroup: (uid: string, target: string) =>
    request<Group>(`/groups/${uid}/merge`, { method: 'POST', body: { target } }),
  createOccupancy: (body: {
    person: string
    location: string
    kind: string
    valid_from?: string | null
    valid_to?: string | null
  }) => request<Occupancy>('/occupancies', { method: 'POST', body }),
  deleteOccupancy: (uid: string) => request<void>(`/occupancies/${uid}`, { method: 'DELETE' }),
  groupOccupancies: (groupUid: string) => request<GroupOccupancy[]>(`/groups/${groupUid}/occupancies`),
  createGroupOccupancy: (
    groupUid: string,
    body: { location: string; kind: string; valid_from?: string | null; valid_to?: string | null },
  ) => request<GroupOccupancy>(`/groups/${groupUid}/occupancies`, { method: 'POST', body }),
  deleteGroupOccupancy: (groupUid: string, uid: string) =>
    request<{ success: boolean }>(`/groups/${groupUid}/occupancies/${uid}`, { method: 'DELETE' }),
  locationTree: () => request<LocationNode[]>('/locations/tree'),
  location: (uid: string) => request<LocationDetail>(`/locations/${uid}`),
  /** `days` clamped server-side to [1, 365], default 30. */
  locationCredentialUsage: (uid: string, days?: number) =>
    request<LocationCredentialUsage>(`/locations/${uid}/credential-usage${qs({ days: days ? String(days) : undefined })}`),
  createLocation: (body: LocationPayload) => request<LocationDetail>('/locations', { method: 'POST', body }),
  updateLocation: (uid: string, body: LocationPayload) =>
    request<LocationDetail>(`/locations/${uid}`, { method: 'PUT', body }),
  deleteLocation: (uid: string) => request<void>(`/locations/${uid}`, { method: 'DELETE' }),
  doors: () => request<Door[]>('/doors'),
  createDoor: (body: DoorCreateBody) => request<Door>('/doors', { method: 'POST', body }),
  updateDoor: (uid: string, body: DoorUpdateBody) => request<Door>(`/doors/${uid}`, { method: 'PUT', body }),
  deleteDoor: (uid: string) => request<{ success: boolean }>(`/doors/${uid}`, { method: 'DELETE' }),
  zones: () => request<Zone[]>('/zones'),
  createZone: (body: ZonePayload) => request<Zone>('/zones', { method: 'POST', body }),
  /** Full-replace: `locations` is the zone's complete location set. */
  updateZone: (uid: string, body: ZonePayload) => request<Zone>(`/zones/${uid}`, { method: 'PUT', body }),
  deleteZone: (uid: string) => request<{ success: boolean }>(`/zones/${uid}`, { method: 'DELETE' }),
  // Wisenet WAVE event forwarding. Writes are admin-only.
  cameraSettings: (uid: string) =>
    request<CameraForwardingSettings>(`/connectors/wisenet/cameras/${uid}/settings`),
  updateCameraSettings: (uid: string, body: CameraForwardingSettingsBody) =>
    request<CameraForwardingSettings>(`/connectors/wisenet/cameras/${uid}/settings`, { method: 'PUT', body }),
  forwardingDefaults: (uid: string) => request<ForwardingDefaults>(`/connectors/wisenet/systems/${uid}/forwarding`),
  updateForwardingDefaults: (uid: string, body: ForwardingDefaults) =>
    request<ForwardingDefaults>(`/connectors/wisenet/systems/${uid}/forwarding`, { method: 'PUT', body }),
  eventBookmarks: (uid: string) => request<EventBookmark[]>(`/connectors/wisenet/events/${uid}/bookmarks`),
  doorCodes: (series?: string, location?: string) =>
    request<DoorCode[]>(`/door-codes${qs({ series, location })}`),
  createDoorCode: (body: DoorCodeBody) => request<DoorCode>('/door-codes', { method: 'POST', body }),
  /** Full-replace (doors + locations union one-shot into the door scope). */
  updateDoorCode: (uid: string, body: DoorCodeBody) =>
    request<DoorCode>(`/door-codes/${uid}`, { method: 'PUT', body }),

  // --- Door code series (rotating codes; admin-gated, roll = operator) ---
  doorCodeSeries: () => request<DoorCodeSeries[]>('/door-code-series'),
  createDoorCodeSeries: (body: DoorCodeSeriesBody) =>
    request<DoorCodeSeries>('/door-code-series', { method: 'POST', body }),
  /** PATCH semantics: only SET fields apply; null clears a nullable field. */
  updateDoorCodeSeries: (uid: string, body: DoorCodeSeriesPatchBody) =>
    request<DoorCodeSeries>(`/door-code-series/${uid}`, { method: 'PATCH', body }),
  deleteDoorCodeSeries: (uid: string) =>
    request<{ success: boolean }>(`/door-code-series/${uid}`, { method: 'DELETE' }),
  rollDoorCodeSeries: (uid: string) =>
    request<RollSummary>(`/door-code-series/${uid}/roll`, { method: 'POST' }),
  accessGroups: () => request<AccessGroup[]>('/access-groups'),
  createAccessGroup: (body: AccessGroupBody) =>
    request<AccessGroup>('/access-groups', { method: 'POST', body }),
  updateAccessGroup: (uid: string, body: AccessGroupBody) =>
    request<AccessGroup>(`/access-groups/${uid}`, { method: 'PUT', body }),
  deleteAccessGroup: (uid: string) =>
    request<{ success: boolean }>(`/access-groups/${uid}`, { method: 'DELETE' }),
  mergeAccessGroup: (uid: string, target: string) =>
    request<AccessGroup>(`/access-groups/${uid}/merge`, { method: 'POST', body: { target } }),
  accessGroupMembers: (uid: string) => request<AccessGroupMember[]>(`/access-groups/${uid}/members`),
  createAccessGroupMember: (uid: string, person: string) =>
    request<DirectAccessGroupMember>(`/access-groups/${uid}/members`, { method: 'POST', body: { person } }),
  deleteAccessGroupMember: (uid: string, memberUid: string) =>
    request<{ success: boolean }>(`/access-groups/${uid}/members/${memberUid}`, { method: 'DELETE' }),
  directoryGrants: (uid: string) => request<DirectoryGrant[]>(`/access-groups/${uid}/directory-grants`),
  createDirectoryGrant: (uid: string, group: string) =>
    request<DirectoryGrant>(`/access-groups/${uid}/directory-grants`, { method: 'POST', body: { group } }),
  deleteDirectoryGrant: (uid: string, grantUid: string) =>
    request<{ success: boolean }>(`/access-groups/${uid}/directory-grants/${grantUid}`, { method: 'DELETE' }),
  accessGroupEffectiveDoors: (uid: string) =>
    request<EffectiveDoor[]>(`/access-groups/${uid}/effective-doors`),
  personEffectiveAccess: (uid: string) => request<PersonEffectiveAccess>(`/persons/${uid}/effective-access`),
  directoryGroupEffectiveAccess: (uid: string) =>
    request<DirectoryGroupEffectiveAccess>(`/directory-groups/${uid}/effective-access`),
  doorPolicies: () => request<DoorPolicy[]>('/door-policies'),
  createDoorPolicy: (body: { group: string; door: string; mode: DoorPolicy['mode']; schedule?: string | null }) =>
    request<DoorPolicy>('/door-policies', { method: 'POST', body }),
  updateDoorPolicy: (uid: string, body: DoorPolicyUpdateBody) =>
    request<DoorPolicy>(`/door-policies/${uid}`, { method: 'PUT', body }),
  deleteDoorPolicy: (uid: string) =>
    request<{ success: boolean }>(`/door-policies/${uid}`, { method: 'DELETE' }),
  memberRules: () => request<MemberRule[]>('/member-rules'),
  createMemberRule: (body: { group: string; location: string }) =>
    request<MemberRule>('/member-rules', { method: 'POST', body }),
  deleteMemberRule: (uid: string) =>
    request<{ success: boolean }>(`/member-rules/${uid}`, { method: 'DELETE' }),
  schedules: (kind?: ScheduleKind) => request<Schedule[]>(`/schedules${qs({ kind })}`),
  createSchedule: (body: ScheduleBody) => request<Schedule>('/schedules', { method: 'POST', body }),
  updateSchedule: (uid: string, body: ScheduleBody) =>
    request<Schedule>(`/schedules/${uid}`, { method: 'PUT', body }),
  deleteSchedule: (uid: string) =>
    request<{ success: boolean }>(`/schedules/${uid}`, { method: 'DELETE' }),
  events: (params: EventsParams = {}) => request<Page<AccessEvent>>(`/events${qs({ ...params })}`),
  matchSuggestions: (status?: string) => request<MatchSuggestion[]>(`/match-suggestions${qs({ status })}`),
  acceptMatchSuggestion: (uid: string) =>
    request<MatchSuggestion>(`/match-suggestions/${uid}/accept`, { method: 'POST' }),
  rejectMatchSuggestion: (uid: string) =>
    request<MatchSuggestion>(`/match-suggestions/${uid}/reject`, { method: 'POST' }),
  credentials: (order?: CredentialOrder) => request<Credential[]>(`/credentials${qs({ order })}`),
  credential: (uid: string) => request<Credential>(`/credentials/${uid}`),
  createCredential: (body: {
    person: string | null
    kind: string
    code: string
    code_base?: 'hex' | 'dec' | null
    status?: string
  }) => request<Credential>('/credentials', { method: 'POST', body }),
  updateCredential: (
    uid: string,
    body: {
      person?: string | null
      kind?: string
      code?: string
      status?: string
    },
  ) => request<Credential>(`/credentials/${uid}`, { method: 'PUT', body }),
  /** Partial edit (issue #54, holder_name removed in #57) — works for
   *  operators, whose `code` is masked (a PUT round-trip would need the
   *  real code). `status` is the only field left: the local lifecycle flip
   *  (lost/returned/active); blocking and unblocking stay on block/unblock
   *  (the API 409s a PATCH into or out of `blocked`). Reporting lost/returned
   *  also disables the card at every vendor right away; the response's
   *  `command` carries that CommandLog (issue #55). */
  patchCredential: (uid: string, body: { status?: string }) =>
    request<CredentialPatchResult>(`/credentials/${uid}`, { method: 'PATCH', body }),
  deleteCredential: (uid: string) => request<void>(`/credentials/${uid}`, { method: 'DELETE' }),
  blockCredential: (uid: string) =>
    request<CommandLog>(`/credentials/${uid}/block`, { method: 'POST' }),
  unblockCredential: (uid: string) =>
    request<CommandLog>(`/credentials/${uid}/unblock`, { method: 'POST' }),
  unknownCredentials: (status?: string) => request<UnknownCredential[]>(`/unknown-credentials${qs({ status })}`),
  convertUnknownCredential: (uid: string) =>
    request<UnknownCredential>(`/unknown-credentials/${uid}/convert`, { method: 'POST' }),
  /** Convert with an explicit holder + kind (the no-body form above is kept for the Overview panel). */
  convertUnknownCredentialFor: (uid: string, body: { person: string; kind: string }) =>
    request<UnknownCredential>(`/unknown-credentials/${uid}/convert`, { method: 'POST', body }),
  ignoreUnknownCredential: (uid: string) =>
    request<UnknownCredential>(`/unknown-credentials/${uid}/ignore`, { method: 'POST' }),
  openDoor: (uid: string) => request<CommandLog>(`/doors/${uid}/open`, { method: 'POST' }),
  commands: () => request<Page<CommandLog>>('/commands'),

  // --- Outgoing webhooks (ops/api.py; mutations are admin-gated) ---
  webhooks: () => request<WebhookSubscription[]>('/webhooks'),
  createWebhook: (body: WebhookSubscriptionBody) =>
    request<WebhookSubscription>('/webhooks', { method: 'POST', body }),
  /** Full-replace INCLUDING the secret (write-only — an edit must re-enter
   *  it). An empty `event_types` subscribes to all event types. */
  updateWebhook: (uid: string, body: WebhookSubscriptionBody) =>
    request<WebhookSubscription>(`/webhooks/${uid}`, { method: 'PUT', body }),
  deleteWebhook: (uid: string) => request<{ success: boolean }>(`/webhooks/${uid}`, { method: 'DELETE' }),
  /** The 50 most recent delivery rows for one subscription. */
  webhookDeliveries: (uid: string) => request<WebhookDelivery[]>(`/webhooks/${uid}/deliveries`),
  /** The global event-type taxonomy — the webhook builder's pick-list. */
  eventTypes: () => request<EventTypeChoice[]>('/event-types'),
  trash: () => request<TrashListResponse>('/trash'),
  restoreTrash: (uid: string) => request<void>(`/trash/${uid}/restore`, { method: 'POST' }),
  purgeTrash: (uid: string) => request<void>(`/trash/${uid}`, { method: 'DELETE' }),
}
